import { useId } from 'react';
import { useIntl } from 'react-intl';
import { Tag, Tooltip, TooltipInteractionEnum } from 'sonata-react-components';

import { usePoiTagOverflow } from '@/hooks/usePoiTagOverflow';

import './heatmapTagBar.scss';

type TagItem = {
  id: string;
  label: string;
};

type Props = {
  tags: TagItem[];
  activeTagIds: string[];
  onTagClick: (tagId: string) => void;
  onAllTagsClick: () => void;
};

const ALL_TAGS_ID = '__all__';

export default function HeatmapTagBar({ tags, activeTagIds, onTagClick, onAllTagsClick }: Readonly<Props>) {
  const overflowId = useId();
  const { formatMessage } = useIntl();
  const allTagsSelected = activeTagIds.length === 0 || activeTagIds.length === tags.length;

  // +1 for the "All brands" tag
  const { containerRef, visibleCount } = usePoiTagOverflow(tags.length + 1);

  // visibleCount includes the "All brands" tag slot (index 0)
  const visibleTagCount = Math.max(0, visibleCount - 1);
  const visibleTags = tags.slice(0, visibleTagCount);
  const overflowTags = tags.slice(visibleTagCount);
  const overflowCount = overflowTags.length;

  return (
    <div className="heatmapTagBar" ref={containerRef}>
      <Tag
        className={`heatmapTagBar__tag${allTagsSelected ? ' heatmapTagBar__tag--active' : ''}`}
        id={ALL_TAGS_ID}
        onClick={onAllTagsClick}
        status={allTagsSelected ? 'enabled' : 'information'}
      >
        {formatMessage({ defaultMessage: 'All brands', id: 'aggregatedReport.heatmapTagBar.allBrands' })}
      </Tag>
      {visibleTags.map(tag => {
        const isActive = activeTagIds.includes(tag.id);
        return (
          <Tag
            className={`heatmapTagBar__tag${isActive ? ' heatmapTagBar__tag--active' : ''}`}
            id={`heatmap-tag-${tag.id}`}
            key={tag.id}
            onClick={() => onTagClick(tag.id)}
            status={isActive ? 'enabled' : 'information'}
          >
            {tag.label}
          </Tag>
        );
      })}
      {overflowCount > 0 && (
        <Tooltip
          config={{
            component: (
              <div className="heatmapTagBar__overflowContent">
                {overflowTags.map(tag => {
                  const isActive = activeTagIds.includes(tag.id);
                  return (
                    <button
                      className={`heatmapTagBar__overflowItem${isActive ? ' heatmapTagBar__overflowItem--active' : ''}`}
                      key={tag.id}
                      onClick={() => onTagClick(tag.id)}
                      type="button"
                    >
                      {tag.label}
                    </button>
                  );
                })}
              </div>
            ),
            interactions: [TooltipInteractionEnum.click],
            padding: '16px',
            placement: 'bottom',
            strategy: 'absolute',
            type: 'contextualMenu'
          }}
        >
          <Tag className="heatmapTagBar__tag heatmapTagBar__tag--overflow" id={overflowId} status="information">
            +{overflowCount}
          </Tag>
        </Tooltip>
      )}
    </div>
  );
}
